/**
 * The position catalogue, in the order a statement is read.
 *
 * Mirrors `AccountGroupKey` in `aumico-be`. The Kontenplan offers these. The statements take their
 * order and structure from the layout the backend sends with the accounts; this order is only their
 * fallback while a backend sends none.
 *
 * ⚠️ Owned by the backend. When a value is added there, it must appear here and in every translation
 * file, or the screen shows a raw key.
 */
export const BALANCE_SHEET_POSITIONS: readonly string[] = [
    'CURRENT_ASSETS_LIQUID',
    'CURRENT_ASSETS_MARKETABLE_SECURITIES',
    'CURRENT_ASSETS_RECEIVABLES',
    'CURRENT_ASSETS_OTHER_RECEIVABLES',
    'CURRENT_ASSETS_INVENTORIES',
    'CURRENT_ASSETS_PREPAID_EXPENSES',
    'NON_CURRENT_ASSETS_FINANCIAL',
    'NON_CURRENT_ASSETS_INVESTMENTS',
    'NON_CURRENT_ASSETS_TANGIBLE',
    'NON_CURRENT_ASSETS_INTANGIBLE',
    'NON_CURRENT_ASSETS_UNPAID_CAPITAL',
    'CURRENT_LIABILITIES_TRADE_PAYABLES',
    'CURRENT_LIABILITIES_INTEREST_BEARING',
    'CURRENT_LIABILITIES_OTHER',
    'CURRENT_LIABILITIES_ACCRUED',
    'NON_CURRENT_LIABILITIES',
    'NON_CURRENT_LIABILITIES_PROVISIONS',
    'EQUITY_SHARE_CAPITAL',
    'EQUITY_LEGAL_CAPITAL_RESERVE',
    'EQUITY_LEGAL_RETAINED_RESERVE',
    'EQUITY_VOLUNTARY_RETAINED_RESERVE',
    'EQUITY_RESULT_FOR_THE_YEAR',
    'EQUITY_TREASURY_SHARES',
];

/** The income-statement positions of the KMU-Kontenrahmen (classes 3 to 9), in account-number order. */
export const INCOME_STATEMENT_POSITIONS: readonly string[] = [
    'INCOME_STATEMENT_PRODUCTION_REVENUE',
    'INCOME_STATEMENT_TRADE_REVENUE',
    'INCOME_STATEMENT_SERVICE_REVENUE',
    'INCOME_STATEMENT_OTHER_REVENUE',
    'INCOME_STATEMENT_OWN_WORK_CAPITALISED',
    'INCOME_STATEMENT_REVENUE_DEDUCTIONS',
    'INCOME_STATEMENT_INVENTORY_CHANGES',
    'INCOME_STATEMENT_MATERIAL_EXPENSE',
    'INCOME_STATEMENT_GOODS_EXPENSE',
    'INCOME_STATEMENT_THIRD_PARTY_SERVICES',
    'INCOME_STATEMENT_PRODUCTION_ENERGY',
    'INCOME_STATEMENT_PURCHASE_DEDUCTIONS',
    'INCOME_STATEMENT_WAGES',
    'INCOME_STATEMENT_SOCIAL_SECURITY',
    'INCOME_STATEMENT_OTHER_PERSONNEL',
    'INCOME_STATEMENT_TEMPORARY_STAFF',
    'INCOME_STATEMENT_PREMISES',
    'INCOME_STATEMENT_MAINTENANCE',
    'INCOME_STATEMENT_VEHICLES',
    'INCOME_STATEMENT_INSURANCE_FEES',
    'INCOME_STATEMENT_ENERGY_DISPOSAL',
    'INCOME_STATEMENT_ADMINISTRATION',
    'INCOME_STATEMENT_IT',
    'INCOME_STATEMENT_ADVERTISING',
    'INCOME_STATEMENT_OTHER_OPERATING',
    'INCOME_STATEMENT_DEPRECIATION',
    'INCOME_STATEMENT_FINANCIAL_EXPENSE',
    'INCOME_STATEMENT_FINANCIAL_INCOME',
    'INCOME_STATEMENT_ANCILLARY_OPERATIONS',
    'INCOME_STATEMENT_OPERATING_PROPERTY',
    'INCOME_STATEMENT_NON_OPERATING',
    'INCOME_STATEMENT_EXTRAORDINARY',
    'INCOME_STATEMENT_DIRECT_TAXES',
    'INCOME_STATEMENT_CLOSING',
];

/** The i18n key of one position, or of the group that holds the unassigned ones. */
export function positionLabelKey(position: string | null): string {
    return position === null
        ? 'abcp.accountSurface.unassignedGroup'
        : `abcp.accountSurface.position.${position}`;
}
